import React, { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { MathfieldElement } from "mathlive";
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
    MdExitToApp,
    MdKeyboardArrowDown,
    MdKeyboardArrowUp,
    MdOutlineBookmark,
    MdOutlineBookmarkBorder,
    MdOutlineLocationOn,
    // MdOutlinePlayCircle,
} from 'react-icons/md';
import { IoMdRadioButtonOn, IoMdRadioButtonOff } from "react-icons/io";
import { IoClose } from "react-icons/io5";
import { RiCheckboxFill, RiCheckboxBlankLine } from "react-icons/ri";
import moment from 'moment';
import Button from 'components/Button';
import TextInput from 'components/TextInput';
import Loader from 'components/Loader';
import axios from 'config/axios';
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { setError } from 'store/error';
import { MULTIPLE_CHOICES } from 'global/constants';
import { getDeviceOS } from 'global/os';
import { applyKatexWrap, flattenKatexUnderlines } from 'global/katexWrap';
import useOutsideClickHandler from 'hooks/useOutsideClickHandler';
import DragAndDropBucketReview from './DragAndDropBucketReview';
import DragAndDropBlankReview from './DragAndDropBlankReview';
import TableGridReview from './TableGridReview';
import './index.css';

import 'katex/dist/katex.min.css';
import renderMathInElement from "katex/dist/contrib/auto-render.js";

const COLORS = {
    'Yellow': 'yellow',
    'Pink': 'pink',
    'Blue': 'blue'
};

function DigitalSHSATReview() {
    const [studentName, setStudentName] = useState('');
    const [testModules, setTestModules] = useState(null);
    const [moduleIndex, setModuleIndex] = useState(0);
    const [questionIndex, setQuestionIndex] = useState(0);
    const [moduleSelectOpen, setModuleSelectOpen] = useState(false);
    const [directionsOpen, setDirectionsOpen] = useState(false);
    const [navigatorOpen, setNavigatorOpen] = useState(false);
    const [showAnswer, setShowAnswer] = useState(false);
    const [explanationOpen, setExplanationOpen] = useState(false);
    const [currentDefinition, setCurrentDefinition] = useState(null);
    const [popupPos, setPopupPos] = useState({ top: 0, left: 0 });
    const [annotations, setAnnotations] = useState([]);
    const [fullPassage, setFullPassage] = useState<{
        html?: string;
        preview?: string;
    } | null>(null);
    const [definitions, setDefinitions] = useState<Array<{
        passageId?: string;
        html?: string;
        preview?: string;
    }>>([]);
    const [questionHelper, setQuestionHelper] = useState(null);
    const [fbQuestionHelper, setFbQuestionHelper] = useState(null);
    const [icQuestionHelper, setIcQuestionHelper] = useState(null);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { organization } = useAppSelector(state => state.organization);
    const { testId } = useParams();
    const moduleSelectRef = useOutsideClickHandler(() => setTimeout(() => setModuleSelectOpen(false), 10));
    const directionsRef = useOutsideClickHandler(() => setTimeout(() => setDirectionsOpen(false), 10));
    const passageContainerRef = useRef(null);
    const navigatorRef = useOutsideClickHandler(() => setTimeout(() => setNavigatorOpen(false), 10));
    const mathfieldRef = useRef<MathfieldElement | null>(null);

    useEffect(() => {
        const keyboard = (window as any).mathVirtualKeyboard;
        // keyboard.visible = false;
        // keyboard.hide()

        // always visible
        const questions = testModules?.[moduleIndex]?.questions;
        if (!questions || questions.length === 0) return;
        if (questions[questionIndex]?.questionType === 11 || questions[questionIndex]?.questionType === 12) {
            mathfieldRef.current.mathVirtualKeyboardPolicy = 'manual'
        } else {
            if (mathfieldRef.current) mathfieldRef.current.mathVirtualKeyboardPolicy = 'auto';
            keyboard.hide();
        }
        
        const mf = mathfieldRef.current;
        if (mf) mf.setAttribute("virtual-keyboard-toolbar", "none");

        if (!keyboard || !mf) return;
        
        if (questions[questionIndex]?.questionType !== 11 && questions[questionIndex]?.questionType !== 12) {
            keyboard.visible = false;
            return;
        }
        
        // keyboard customization
        if (questions[questionIndex]?.questionType === 11) {
            keyboard.layouts = {
                rows: [
                    ["1","2","3","4","5","x","y",""],
                    ["6","7","8","9","0","+","−","\\sqrt{#0}"],
                    ["\\%","−",".","\\frac{#0}{#0}","{#0}\\frac{#0}{#0}","\\cdot","\\div","\\sqrt[#0]{#0}"],
                    ["","{#0}^{#0}","({#0})","\\le","\\lt","=","\\ge","\\gt"],
                    ["\\pi","","","","","","","\\lvert{#@}\\rvert"]
                ]
            };
            keyboard.show();
            keyboard.visible = true;
            // Add hover tooltips
            setTimeout(() => {
                const keys = document.querySelectorAll(".digitalSHSATReview__eeContainer .MLK__keycap");
                let minusCount = 0; 

                keys.forEach((key) => {
                    const k = key.textContent || "";
                    let tooltip = "";
                    switch (k) {
                        case "1": tooltip = "one"; break;
                        case "2": tooltip = "two"; break;
                        case "3": tooltip = "three"; break;
                        case "4": tooltip = "four"; break;
                        case "5": tooltip = "five"; break;
                        case "6": tooltip = "six"; break;
                        case "7": tooltip = "seven"; break;
                        case "8": tooltip = "eight"; break;
                        case "9": tooltip = "nine"; break;
                        case "0": tooltip = "zero"; break;
                        case "x": tooltip = "letter x"; break;
                        case "y": tooltip = "letter y"; break;
                        case "+": tooltip = "add"; break;
                        case "−":
                        case "-":
                            minusCount++;
                            tooltip = minusCount === 1 ? "subtract" : "negative sign";
                            break;
                        case "÷": tooltip = "divide"; break;
                        case ".": tooltip = "decimal point"; break;
                        case "%": tooltip = "percent sign"; break;
                        case "=": tooltip = "equal"; break;
                        case "≤": tooltip = "less than or equal"; break;
                        case "<": tooltip = "less than"; break;
                        case "≥": tooltip = "greater than or equal"; break;
                        case ">": tooltip = "greater than"; break;
                        case "π": tooltip = "constant pi"; break;
                        case "⋅": tooltip = "multiply dot"; break;
                        case "√▢​": tooltip = "square root"; break;
                        case "▢√▢​": tooltip = "general root"; break;
                        case "▢▢": tooltip = "exponent"; break;
                        case "(▢)": tooltip = "parentheses"; break;
                        case "∣■∣": tooltip = "absolute value"; break;
                        case "▢▢​": tooltip = "fraction"; break;
                        case "▢▢▢​": tooltip = "mixed number"; break;
                        default: tooltip = ""; // empty for blank keys
                    }
                    (key as HTMLElement).setAttribute("data-tooltip", tooltip);
                });
            }, 100);

        } else if (questions[questionIndex]?.questionType === 12) {
            keyboard.layouts = {
                rows: [
                    ["1","2","3","4","5","","",""],
                    ["6","7","8","9","0","","",""],
                    ["\\%","-",".","\\frac{#0}{#0}","{#0}\\frac{#0}{#0}","","",""]
                ]
            }; 
            keyboard.show();
            keyboard.visible = true;   
            // Add hover tooltips
            setTimeout(() => {
                const keys = document.querySelectorAll(".digitalSHSATReview__eeContainer .MLK__keycap");
                keys.forEach((key) => {
                    const k = key.textContent || "";
                    let tooltip = "";
                    switch (k) {
                        case "1": tooltip = "one"; break;
                        case "2": tooltip = "two"; break;
                        case "3": tooltip = "three"; break;
                        case "4": tooltip = "four"; break;
                        case "5": tooltip = "five"; break;
                        case "6": tooltip = "six"; break;
                        case "7": tooltip = "seven"; break;
                        case "8": tooltip = "eight"; break;
                        case "9": tooltip = "nine"; break;
                        case "0": tooltip = "zero"; break;
                        case "−": tooltip = "negative sign"; break;
                        case ".": tooltip = "decimal point"; break;
                        case "%": tooltip = "percent sign"; break;
                        case "▢▢​": tooltip = "fraction"; break;
                        case "▢▢▢​": tooltip = "mixed number"; break;
                        default: tooltip = ""; // empty for blank keys
                    }
                    (key as HTMLElement).setAttribute("data-tooltip", tooltip);
                });
            }, 100);        
        }

        // remove toggle menu buttons, keyboard location, keyboard styling
        const style = document.createElement("style");
        style.innerHTML = `
            math-field::part(menu-toggle),
            math-field::part(virtual-keyboard-toggle) { display: none !important; }

            .digitalSHSATReview__eeContainer { position: relative; }
            .digitalSHSATReview__eeContainer .ML__virtual-keyboard {
                position: absolute !important;
                top: calc(100% + 8px) !important;
                left: 0 !important;
                width: 620px !important;
                z-index: 999 !important;
            }
            .digitalSHSATReview__eeContainer .MLK__backdrop {
                background: none;
            }
            .digitalSHSATReview__eeContainer .MLK__toolbar { display: none; }
            .digitalSHSATReview__eeContainer .MLK__keycap {
                border: 1px solid #676767;
                flex: 1;
                cursor: none;
                pointer-events: none;
            }
            .digitalSHSATReview__eeContainer .ML__keyboard [data-tooltip] {
                position: relative;
                pointer-events: none;
            }
            .ML__latex .ML__vlist-t2,
            .ML__delim-size1 {
                font-size: 16px;
            }
            .digitalSHSATReview__eeContainer .MLK__toolbar { 
                justify-content: flex-start; 
            }
            .digitalSHSATReview__eeContainer .MLK__plate {
                border: 1px solid #bdbdbd;
                padding: 4px;
            }
            .digitalSHSATReview__eeContainer :where(.MLK__rows > .MLK__row div) {
                border: 1px solid #bdbdbd;
                border-radius: 1px;
                font-size: 18px;
                background: white;
            }
            .digitalSHSATReview__eeContainer .MLK__rows > .MLK__row div:empty {
                pointer-events: none;
                opacity: 0.5;         
                background-color: white;
                border: 1px solid #e0e0e0ff;
            }
            .digitalSHSATReview__eeContainer .MLK__rows {
                gap: 5px;
            }    
            .digitalSHSATReview__eeContainer .MLK__rows > .MLK__row {
                gap: 5px;
                justify-content: flex-start;
            }    
            .digitalSHSATReview__eeContainer .MLK__row:nth-child(-n+2) div:nth-child(-n+5) {
                background-color: #eeeeeec7 !important;
            }
            /* High-contrast keycap/toolbar labels (MathLive's dark-theme default
               glyph color is near-white on these white keys). */
            .digitalSHSATReview__eeContainer {
                --keycap-text: #1a1a1a;
                --keycap-text-active: #1a1a1a;
                --keycap-text-hover: #1a1a1a;
                --keycap-text-pressed: #1a1a1a;
                --keycap-secondary-text: #1a1a1a;
                --keyboard-toolbar-text: #1a1a1a;
                --keyboard-toolbar-text-active: #1a1a1a;
            }
            .digitalSHSATReview__eeContainer .MLK__keycap:not(:empty),
            .digitalSHSATReview__eeContainer .MLK__keycap:not(:empty) *,
            .digitalSHSATReview__eeContainer .ML__latex,
            .digitalSHSATReview__eeContainer .ML__latex * {
                color: #1a1a1a !important;
            }
            .digitalSHSATReview__eeContainer .MLK__toolbar .action svg {
                fill: currentColor !important;
            }
        `;
        document.head.appendChild(style);

        // keyboard location
        const container = document.querySelector(".digitalSHSATReview__eeContainer");
        let observer: MutationObserver | null = null;
        if (container) {
            keyboard.container = container;
            keyboard.alwaysVisible = true;
            keyboard.floater = false;
            keyboard.autoDismiss = false;
            keyboard.show();
        }
        setTimeout(() => {
            const vk = container?.querySelector(".ML__keyboard") as HTMLElement;
            if (vk) {
                // keyboard location styling
                vk.style.height = "260px";
                vk.style.pointerEvents = "auto";
                vk.style.opacity = "1";
                vk.style.visibility = "visible";
                vk.style.removeProperty('--_keyboard-height'); 
                vk.style.height = '300px';    

                // always keyboard visible
                observer = new MutationObserver(() => {
                    if (
                        vk.style.display === "none" ||
                        vk.style.visibility === "hidden" ||
                        vk.style.opacity === "0"
                    ) {
                        vk.style.display = "block";
                        vk.style.visibility = "visible";
                        vk.style.opacity = "1";
                        vk.style.pointerEvents = "auto";
                    }
                });
                observer.observe(vk, {
                    attributes: true,
                    attributeFilter: ["style"],
                    subtree: false,
                });
            }
            if (mf) {
                // always initially
                mf.setAttribute("readonly", "true"); // 입력 불가
                mf.style.caretColor = "transparent";  // 커서 숨기기
                mf.style.userSelect = "none";         // 텍스트 선택 불가
                mf.style.pointerEvents = "none";      // 클릭 이벤트 차단
                // input focus styl
                
                // always keyboard visible
                const shadow = mf.shadowRoot;
                if (shadow) {
                    const textarea = shadow.querySelector("textarea");
                    if (textarea) {
                        (textarea as HTMLTextAreaElement).disabled = true;
                        (textarea as HTMLTextAreaElement).blur();
                        (textarea as HTMLTextAreaElement).setAttribute("readonly", "true");
                        (textarea as HTMLTextAreaElement).setAttribute("tabindex", "-1");
                        (textarea as HTMLTextAreaElement).style.pointerEvents = "none";
                    }
                }
                const cancelEvent = (e: Event) => {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    return false;
                };

                ["focus", "focusin", "mousedown", "mouseup", "touchstart", "click"].forEach((evt) =>
                    mf.addEventListener(evt, cancelEvent, true)
                );
            }
        }, 50);
        return () => {
            // style 제거
            document.head.removeChild(style);
            // observer 해제
            if (observer) observer.disconnect();
        };
    }, [questionIndex]);

    useEffect(() => {
        const mf = mathfieldRef.current;
        if (!mf) return;
        let stAnswer = (answers[questionIndex].answer ?? "").replace(/\\\\/g, '\\'); 
        let correctAnswer = testModules[moduleIndex].questions[questionIndex].answers.map(ans => ans.replace(/\\\\/g, '\\')).join(', ');

        let separator = stAnswer && showAnswer? " / " : "";
        let combined = stAnswer + separator;
        if (showAnswer) {
            combined += "\\; \\color{green}{" + correctAnswer + "}"; 
        }

        mf.setValue(combined);
    }, [showAnswer, questionIndex]);

    const prevQuestion = () => {
        if (questionIndex > 0) {
            setQuestionIndex(questionIndex - 1);
        }
        else if (moduleIndex > 0) {
            setQuestionIndex(testModules[moduleIndex - 1].questions.length - 1);
            setModuleIndex(moduleIndex - 1);
        }
    }

    const nextQuestion = () => {
        if (questionIndex < testModules[moduleIndex].questions.length - 1) {
            setQuestionIndex(questionIndex + 1);
        }
        else if (moduleIndex < testModules.length - 1) {
            setModuleIndex(moduleIndex + 1);
            setQuestionIndex(0);
        }
        else {
            // exit the review
            navigate(-1);
        }
    }

    const goToQuestion = (index: number) => {
        setQuestionIndex(index);

        let passages = document.getElementById('digitalSHSATReview__passages');
        let question = document.getElementById('digitalSHSATReview__questionChoices');

        if (passages) passages.scrollTop = 0;
        if (question) question.scrollTop = 0;
    }

    const formatTime = (time: number) => {
        const duration = moment.duration(time, 'seconds');

        const hours = duration.hours();
        const minutes = (hours && duration.minutes() < 10) ? `0${duration.minutes()}` : duration.minutes();
        const seconds = duration.seconds() < 10 ? `0${duration.seconds()}` : duration.seconds();

        return hours ? `${hours}:${minutes}:${seconds}` : `${minutes}:${seconds}`;
    }

    // definition
    const handleDefinition = () => {
        const selection = window.getSelection();
        if (!selection) {
            return;
        }

        const {
            anchorNode,
            anchorOffset,
            focusNode,
            focusOffset
        } = selection;

        const passages = document.getElementsByClassName('digitalSHSATReview__Definition');
        const passage = Array.from(passages).find(passage => passage.contains(anchorNode) && passage.contains(focusNode));
        if (!passage) {
            return;
        }

        let startOffset = 0;
        let endOffset = 0;

        for (const childNode of Array.from(passage.childNodes)) {
            let childLength = childNode.textContent.length;

            if (childNode === anchorNode) {
                startOffset += anchorOffset;
                break;
            } 

            else if (childNode.contains(anchorNode)) {
                const childText = childNode.textContent;
                const anchorText = anchorNode.textContent;
                const indexInChild = childText.indexOf(anchorText);
                if(indexInChild === -1) {
                    console.error("anchorText not found inside childText");
                    break;
                }
                const lengthBeforeAnchor = indexInChild + anchorOffset;
                startOffset += lengthBeforeAnchor;
                break;
            }
            else {
                startOffset += childLength;
            }
        }

        for (const childNode of Array.from(passage.childNodes)) {
            let childLength = childNode.textContent.length;

            if (childNode === focusNode) {
                endOffset += focusOffset;
                break;
            } 
            else if (childNode.contains(focusNode)) {
                const childText = childNode.textContent;
                const focusText = focusNode.textContent;
                const indexInChild = childText.indexOf(focusText);
                if(indexInChild === -1) {
                    console.error("focusText not found inside childText");
                    break;
                }
                const lengthBeforeAnchor = indexInChild + focusOffset;
                endOffset += lengthBeforeAnchor;
                break;
            } 
            else {
                endOffset += childLength;
            }
        }

        if (startOffset > endOffset) {
            const temp = startOffset;
            startOffset = endOffset;
            endOffset = temp;
        }

        if (startOffset > endOffset) {
            [startOffset, endOffset] = [endOffset, startOffset];
        }

        // call definition
        const definitions = questions[questionIndex]?.passages?.indices
        definitions.forEach((def) => {
            const {startIndex, endIndex, definition} = def
            if (startOffset >= startIndex && endOffset <= endIndex) {
                setCurrentDefinition({
                    ...def,
                    _updatedAt: Date.now() // 강제로 매번 달라지게 하는 값
                });
            }
        })
    }

    useEffect(() => {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();

        const popupWidth = 700; 
        const popupHeight = 100;
        const gap = 8;

        // selection 영역 위쪽에 뜨도록
        const top = rect.top - popupHeight - gap;
        const left = (rect.left + rect.right) / 2 - popupWidth / 2 + 160;

        setPopupPos({ top, left });
    }, [currentDefinition]);

    // Definition
    const generateDefinitionLink = ({id: passageId, indices, passageFigures = []}) => {
        for (let j = 0; j < indices.length; j++){
            const passage = document.getElementById(passageId);
            let passageHTML = '';
            let passagePreview = '';
            const usedFigureIndices = new Set();
            const { startIndex, endIndex } = indices[j];
            let startOffset = startIndex
            let endOffset = endIndex
            let lastIndex = 0;

            for (let i = 0; i < passage.childNodes.length; i++) {
                const childNode = passage.childNodes[i];

                const addMarkTags = (node) => {
                    if (node.outerHTML) {
                        const { innerHTML, outerHTML } = node;
                        const childOffset = outerHTML.indexOf('>') + 1;
    
                        passageHTML += outerHTML.slice(0, childOffset);
    
                        for (let i = 0; i < node.childNodes.length; i++) {
                            const child = node.childNodes[i];
                            addMarkTags(child);
                        }
    
                        passageHTML += outerHTML.slice(childOffset + innerHTML.length);
                    }
                    else {
                        // 이미지 삽입을 위한 passageFigure 검사
                        const currentFigure = passageFigures.find(fig => fig.index === lastIndex && !usedFigureIndices.has(fig.index));
                        if (currentFigure) {
                            usedFigureIndices.add(currentFigure.figure.index);
                            passageHTML += `
                                <div style="width:100%; overflow:hidden; margin:10px 0;"><img src="${currentFigure.figure.image}" alt="figure" style="width:100%; height:100%; object-fit:contain;" /></div>
                            `;                    
                        }
                        const { textContent } = node;
    
                        if (startOffset >= lastIndex && endOffset <= lastIndex + textContent.length) {
                            passageHTML += textContent.slice(0, startOffset - lastIndex);
                            passageHTML += '<mark class="digitalSHSATReview__definitionMark" data-tooltip="?">';
                            passageHTML += textContent.slice(startOffset - lastIndex, endOffset - lastIndex);
                            passageHTML += '</mark>';
                            passageHTML += textContent.slice(endOffset - lastIndex, textContent.length);
                            passagePreview += textContent.slice(startOffset - lastIndex, endOffset - lastIndex);
                        }
                        else if (startOffset >= lastIndex && startOffset < lastIndex + textContent.length) {
                            passageHTML += textContent.slice(0, startOffset - lastIndex);
                            passageHTML += '<mark class="digitalSHSATReview__definitionMark" data-tooltip="?">';
                            passageHTML += textContent.slice(startOffset - lastIndex, textContent.length);
                            passageHTML += '</mark>';
                            passagePreview += textContent.slice(startOffset - lastIndex, textContent.length);
                        }
                        else if (endOffset >= lastIndex && endOffset < lastIndex + textContent.length) {
                            passageHTML += '<mark class="digitalSHSATReview__definitionMark" data-tooltip="?">';
                            passageHTML += textContent.slice(0, endOffset - lastIndex);
                            passageHTML += '</mark>';
                            passageHTML += textContent.slice(endOffset - lastIndex, textContent.length);
                            passagePreview += textContent.slice(0, endOffset - lastIndex);
                        }
                        else if (startOffset <= lastIndex && endOffset >= lastIndex + textContent.length) {
                            passageHTML += '<mark class="digitalSHSATReview__definitionMark" data-tooltip="?">';
                            passageHTML += textContent;
                            passageHTML += '</mark>';
                            passagePreview += textContent;
                        }
                        else {
                            passageHTML += textContent;
                        }
    
                        lastIndex += textContent.length;
                    }
                }
    
                addMarkTags(childNode);
            }
            const currentFigure = passageFigures.find(fig => fig.index === lastIndex && !usedFigureIndices.has(fig.index));
            if (currentFigure) {
                passageHTML += `
                    <div style="width:100%; overflow:hidden; margin:10px 0;"><img src="${currentFigure.figure.image}" alt="figure" style="width:100%; height:100%; object-fit:contain;" /></div>
                `;                    
            }
            
            setDefinitions(prev => [...(prev || []), { 
                passageId: passageId,
                html: passageHTML, 
                preview: passagePreview 
            }]);
        }

    };

    const getPassageWithFigure = ({id: passageId, passageFigures = []}) => {
        const passage = document.getElementById(passageId);
        let passageHTML = '';
        let lastIndex = 0;
        const usedFigureIndices = new Set();

        for (let i = 0; i < passage.childNodes.length; i++) {
            const childNode = passage.childNodes[i];
            // add open tags first while see the text
            const addMarkTags = (node) => {
                if (node.outerHTML) {
                    const { innerHTML, outerHTML } = node;
                    const childOffset = outerHTML.indexOf('>') + 1;

                    passageHTML += outerHTML.slice(0, childOffset);

                    for (let i = 0; i < node.childNodes.length; i++) {
                        const child = node.childNodes[i];
                        addMarkTags(child);
                    }

                    passageHTML += outerHTML.slice(childOffset + innerHTML.length);
                } 
                else {
                    // 이미지 삽입을 위한 passageFigure 검사
                    const currentFigure = passageFigures.find(fig => fig.index === lastIndex && !usedFigureIndices.has(fig.index));
                    if (currentFigure) {
                        usedFigureIndices.add(currentFigure.figure.index);
                        passageHTML += `
                            <div style="width:100%; overflow:hidden; margin:10px 0;"><img src="${currentFigure.figure.image}" alt="figure" style="width:100%; height:100%; object-fit:contain;" /></div>
                        `;                    
                    }
                    const { textContent } = node;
                    passageHTML += textContent;
                    lastIndex += textContent.length;
                }  
            }

            addMarkTags(childNode);
        }

        const currentFigure = passageFigures.find(fig => fig.index === lastIndex && !usedFigureIndices.has(fig.index));
        if (currentFigure) {
            passageHTML += `
                <div style="width:100%; overflow:hidden; margin:10px 0;"><img src="${currentFigure.figure.image}" alt="figure" style="width:100%; height:100%; object-fit:contain;" /></div>
            `;                    
        }

        setFullPassage({
            html: passageHTML
        });

    };

    const generateAnnotation = () => {
        const newAnnotations = [ ...testModules[moduleIndex].answers[questionIndex].annotations ];

        for (let i = 0; i < newAnnotations.length; i++) {
            const {
                passageId,
                startIndex,
                endIndex
            } = newAnnotations[i];

            const passage = document.getElementById(`passage ${passageId}`);
            if (!passage) {
                return;
            }

            let passageHTML = '';
            let passagePreview = '';
            let lastIndex = 0;
            for (let i = 0; i < passage.childNodes.length; i++) {
                const childNode = passage.childNodes[i];
    
                const addMarkTags = (node) => {
                    if (node.nodeName === 'IMG') {
                        passageHTML += node.outerHTML;
                    }
                    else if (node.outerHTML) {
                        const { innerHTML, outerHTML } = node;
                        const childOffset = outerHTML.indexOf('>') + 1;
    
                        passageHTML += outerHTML.slice(0, childOffset);
    
                        for (let i = 0; i < node.childNodes.length; i++) {
                            const child = node.childNodes[i];
                            addMarkTags(child);
                        }
    
                        passageHTML += outerHTML.slice(childOffset + innerHTML.length);
                    }
                    else {
                        const { textContent } = node;
    
                        if (startIndex >= lastIndex && endIndex <= lastIndex + textContent.length) {
                            passageHTML += textContent.slice(0, startIndex - lastIndex);
                            passageHTML += '<mark>';
                            passageHTML += textContent.slice(startIndex - lastIndex, endIndex - lastIndex);
                            passageHTML += '</mark>';
                            passageHTML += textContent.slice(endIndex - lastIndex, textContent.length);
                            passagePreview += textContent.slice(startIndex - lastIndex, endIndex - lastIndex);
                        }
                        else if (startIndex >= lastIndex && startIndex < lastIndex + textContent.length) {
                            passageHTML += textContent.slice(0, startIndex - lastIndex);
                            passageHTML += '<mark>';
                            passageHTML += textContent.slice(startIndex - lastIndex, textContent.length);
                            passageHTML += '</mark>';
                            passagePreview += textContent.slice(startIndex - lastIndex, textContent.length);
                        }
                        else if (endIndex >= lastIndex && endIndex < lastIndex + textContent.length) {
                            passageHTML += '<mark>';
                            passageHTML += textContent.slice(0, endIndex - lastIndex);
                            passageHTML += '</mark>';
                            passageHTML += textContent.slice(endIndex - lastIndex, textContent.length);
                            passagePreview += textContent.slice(0, endIndex - lastIndex);
                        }
                        else if (startIndex <= lastIndex && endIndex >= lastIndex + textContent.length) {
                            passageHTML += '<mark>';
                            passageHTML += textContent;
                            passageHTML += '</mark>';
                            passagePreview += textContent;
                        }
                        else {
                            passageHTML += textContent;
                        }
    
                        lastIndex += textContent.length;
                    }
                }
    
                addMarkTags(childNode);
            }

            newAnnotations[i].html = passageHTML;
            newAnnotations[i].preview = passagePreview;
        }

        setAnnotations(newAnnotations);
    }

    // Question Helper
    const generateQuestionHelper = ({ questionHelper, hotText = [], correctAnswers = [], studentAnswers = [] }) => {
        if (!questionHelper) return '';

        let html = questionHelper.questionHelper;
        // find [[ ... ]] 
        const regex = /\[\[(.*?)\]\]/g;
        let counter = 0;
        // 매칭되는 부분을 span으로 감싸기
        html = html.replace(regex, (_, match) => {
            const index = counter++;
            const isSelected = studentAnswers.includes(index);
            const className = isSelected? (correctAnswers.includes(index)? (showAnswer? " correct" : " selected"): " inCorrect" ): (showAnswer && correctAnswers.includes(index)? " correct": "");
            return `<div class="digitalSHSATReview__questionHelperHotText${className}">${match}</div>`;
        });
        setQuestionHelper(html);
    };

    // Fill in the Blank Question Helper
    const generateFBQuestionHelper = ({ questionHelper, correctAnswers = [], studentAnswers = [] }) => {
        if (!questionHelper) return '';
        // index별로 답을 묶어줌
        const groupedAnswers = {};
        const groupedCorrects = {};
        studentAnswers.forEach(([idx, val]) => {
            if (!groupedAnswers[idx]) groupedAnswers[idx] = [];
            groupedAnswers[idx].push(val);
        });
        correctAnswers.forEach(([idx, val]) => {
            if (!groupedCorrects[idx]) groupedCorrects[idx] = [];
            groupedCorrects[idx].push(val);
        });

        let html = questionHelper.questionHelper;
        const regex = /\[\[(.*?)\]\]/g;
        let counter = 0;

        // 매칭되는 부분을 span으로 감싸기
        html = html.replace(regex, (_, match) => {
            const index = counter++;
            const studentVals = groupedAnswers[index] || []; 
            const correctVals = groupedCorrects[index] || []; 
            const studentAnswer = studentVals.length > 0 ? studentVals.join(", ") : "";
            const correctAnswer = correctVals.length > 0 ? correctVals.join(", ") : "";
            // const value = showAnswer? 
            const width = Math.max(50, correctVals.reduce((acc, ans) => Math.max(acc, ans?.length || 0), 0) * 20 + 30)
            if (showAnswer) {
                return `
                    <span class="digitalSHSATReview__questionHelperFBBox" style="display:inline-block; width:${width}px">
                        <span class="digitalSHSATReview__fbStudentAnswer">${studentAnswer}${studentAnswer && ' / '}</span>
                        <span class="digitalSHSATReview__fbCorrectAnswer">${correctAnswer}</span>
                    </span>
                `;
            } else {
                return `<input class="digitalSHSATReview__questionHelperFB" style="width:${width}px" data-index="${index}" value="${studentAnswer}" />`;
            }        
    });
        setFbQuestionHelper(html);
    };

    // Inline Choice Question Helper
    const generateICQuestionHelper = ({ questionHelper, inlineChoices = [], correctAnswers = [], studentAnswers = [] }) => {
        if (!questionHelper) return '';
        // index별로 답을 묶어줌
        const groupedAnswers = {};
        const groupedCorrect = {};
        studentAnswers.forEach(([idx, val]) => {
            if (!groupedAnswers[idx]) groupedAnswers[idx] = [];
            groupedAnswers[idx].push(val);
        });
        correctAnswers.forEach(([idx, val]) => {
            if (!groupedCorrect[idx]) groupedCorrect[idx] = [];
            groupedCorrect[idx].push(val);
        });

        let html = questionHelper.questionHelper;
        // find [[ ... ]] 
        const regex = /\[\[(.*?)\]\]/g;
        let counter = 0;

        // 매칭되는 부분을 span으로 감싸기
        html = html.replace(regex, (_, match) => {
            const index = counter++;

            // index별 inlineChoices 찾아오기
            const choices = inlineChoices.filter(choice => choice.index === index);
            // ~8px/char approximates the rendered option width; cap at 320px so a
            // long phrase option (e.g. a 36-char answer) sizes to fit but doesn't
            // overflow the line and wrap the dropdown onto its own row. Longer
            // options truncate in the closed select; the popup still shows them full.
            const longest = choices.length > 0 ? Math.max(...choices.map(c => c.answer?.length || 0)) : 0;
            const width = Math.min(320, Math.max(70, longest * 8));
            const optionsHtml = choices
                .map(
                    (choice) => {
                        const studentAnswer = groupedAnswers[index]?.includes(choice.answer);
                        const correctAnswer = groupedCorrect[index]?.includes(choice.answer);
                        let className = ''
                        if (showAnswer && correctAnswer) {
                            className = "digitalSHSATReview__fbCorrectAnswer";
                        } else if (studentAnswer && correctAnswer) {
                            className = "digitalSHSATReview__fbCorrectAnswer";
                        } else if (studentAnswer && !correctAnswer) {
                            className = "digitalSHSATReview__fbIncorrectAnswer";
                        }
                        return `<span class="${className}" value="${choice.answer}">${choice.answer}</span>`
                })
                .join(", ");

            return `<span class="digitalSHSATReview__questionHelperIC" style="width:${width}px" data-index="${index}">
                        ${optionsHtml}
                    </span>`;
        });
        setIcQuestionHelper(html);
    };

    const renderKatex = () => {
        const katex = document.getElementsByClassName('katex');
        for (let i = 0; i < katex.length; i++) {
            renderMathInElement(katex[i], {
                delimiters: [
                    {left: '$$', right: '$$', display: true},
                    {left: '$', right: '$', display: false},
                    {left: '\\(', right: '\\)', display: false},
                    {left: '\\[', right: '\\]', display: true}
                ],
                trust: true
            });

            katex[i].innerHTML = katex[i].innerHTML.replaceAll('&nbsp;', ' ');
        }

        // NOTE: unlike the module, review wraps Mathematics too. Left as-is —
        // the array guard inside applyKatexWrap is what stopped that from
        // breaking math layout, and narrowing the subject test is a separate
        // question (see docs/bugfix/20260919-shsat-array-passage-wrap-overlap-plan.md).
        applyKatexWrap(document, {
            // Optional-chained: hoisting this out of the loop made it eager, and
            // testModules can still be null on an early render (crashed tutor's
            // equivalent screen with "Cannot read properties of null").
            enabled: testModules?.[moduleIndex]?.subject === 'English Language Arts'
                || testModules?.[moduleIndex]?.subject === 'Mathematics',
        });

        flattenKatexUnderlines(document);

        const math = document.querySelectorAll('.katex-mathml');
        for (let i = 0; i < math.length; i++) {
            math[i].remove();
        }
    }

    const dragMove = (element: HTMLElement, body: HTMLElement, parent: HTMLElement, padding: number = 0) => {
        let pos1 = 0, pos2 = 0, pos3 = 0, pos4 = 0;
        const dragMouseDown = (event) => {
            event.preventDefault();
            // if (element.id!=='magnifierDrag') {
            //     const element2 = document.getElementById(`${element.id}2`);
            //     const allElements = document.querySelectorAll('.draggable-element'); // 드래그 가능한 모든 요소들
            //     allElements.forEach((el) => {
            //         const draggableElement = el as HTMLElement;
            //         draggableElement.style.zIndex = '2';
            //     });
            //     element.style.zIndex = '3'
            //     element2.style.zIndex = '3'
            // }
            if (event.type === 'mousedown') {
                pos3 = event.clientX;
                pos4 = event.clientY;
                document.onmousemove = dragElement;
                document.onmouseup = closeDragElement;
            } else if (event.type === 'touchstart') {
                pos3 = event.touches[0].clientX;
                pos4 = event.touches[0].clientY;
                document.ontouchmove = dragElement;
                document.ontouchend = closeDragElement;
            }
        };
    
        const dragElement = (event) => {
            event.preventDefault();
    
            if (event.type === 'mousemove') {
                pos1 = pos3 - event.clientX;
                pos2 = pos4 - event.clientY;
                pos3 = event.clientX;
                pos4 = event.clientY;

            } else if (event.type === 'touchmove') {
                pos1 = pos3 - event.touches[0].clientX;
                pos2 = pos4 - event.touches[0].clientY;
                pos3 = event.touches[0].clientX;
                pos4 = event.touches[0].clientY;
            }

            // 🔹 부모 요소의 크기 가져오기
            const parentRect = parent.getBoundingClientRect();
            const elementRect = element.getBoundingClientRect();

            // 🔹 새로운 위치 계산 (부모 요소 안에 머물도록 제한)
            let newTop = element.offsetTop - pos2;
            let newLeft = element.offsetLeft - pos1;

            newTop = Math.max(padding, Math.min(newTop, parentRect.height - elementRect.height - padding));
            newLeft = Math.max(padding, Math.min(newLeft, parentRect.width - elementRect.width - padding));

            //🔹 새로운 위치 적용
            element.style.top = `${newTop}px`;
            element.style.left = `${newLeft}px`;
        };
    
        const closeDragElement = () => {
            document.onmouseup = null;
            document.onmousemove = null;
            document.ontouchend = null;
            document.ontouchmove = null;
        };
        

        body.onmousedown = (event) => {
            dragMouseDown(event);
        };
    
        body.ontouchstart = (event) => {
            dragMouseDown(event);
        };
    };

    useEffect(() => {
        axios.get(`tests/digital/shsat/review/${testId}/`)
            .then(res => {
                setStudentName(res.data.studentName);
                setTestModules(res.data.testModules);
            })
            .catch(error => {
                // console.log(error);
                dispatch(setError(error));
            });
    }, []);

    useEffect(()=>{
        // definition move
        const definitionDragEl = document.getElementById('definitionDrag');
        const definitionHeaderEl = document.getElementById('definitionHeader');
        const definitionParentEl = document.getElementById('digitalSHSATReviewBox');
        if (definitionDragEl && definitionHeaderEl && definitionParentEl) {
            dragMove(definitionDragEl, definitionHeaderEl, definitionParentEl);
        }
    }, [currentDefinition])

    useEffect(() => {
        // explanation move
        const explanationDragEl = document.getElementById('explanationDrag');
        const explanationHeaderEl = document.getElementById('explanationHeader');
        const explanationParentEl = document.getElementById('digitalSHSATReviewBox');
        if (explanationDragEl && explanationHeaderEl && explanationParentEl) {
            dragMove(explanationDragEl, explanationHeaderEl, explanationParentEl);
        }
    }, [explanationOpen])

    useLayoutEffect(() => {
        if (testModules) {
            renderKatex();
            if (testModules[moduleIndex].questions[questionIndex].passages) {
                generateAnnotation();
                setDefinitions([]);
                setFullPassage(null);
                const passageData = testModules[moduleIndex].questions[questionIndex]?.passages;
                if (passageData) {
                    const { id, indices, passageFigures } = testModules[moduleIndex].questions[questionIndex].passages
                    if (indices) generateDefinitionLink({ id, indices, passageFigures });  
                    getPassageWithFigure({ id, passageFigures });
                }
            }
            // question helper
            if (testModules[moduleIndex].questions[questionIndex]?.questionHelper) {
                if (testModules[moduleIndex].questions[questionIndex]?.questionType === 4) {
                    generateQuestionHelper({ 
                        questionHelper: testModules[moduleIndex].questions[questionIndex]?.questionHelper, 
                        hotText: testModules[moduleIndex].questions[questionIndex]?.hotText || [],
                        correctAnswers: testModules[moduleIndex].questions[questionIndex]?.hotText?.filter(ht => ht.isCorrect).map(ht => ht.index) || [],
                        studentAnswers: testModules[moduleIndex].answers[questionIndex]?.answer as any[] || []
                    });
                } else if (testModules[moduleIndex].questions[questionIndex]?.questionType === 7) {
                    generateFBQuestionHelper({ 
                        questionHelper: testModules[moduleIndex].questions[questionIndex]?.questionHelper, 
                        correctAnswers: testModules[moduleIndex].questions[questionIndex]?.answers || [],
                        studentAnswers: testModules[moduleIndex].answers[questionIndex]?.answer as any[] || []                    
                    });
                } else if (testModules[moduleIndex].questions[questionIndex]?.questionType === 9) {
                    generateICQuestionHelper({ 
                        questionHelper: testModules[moduleIndex].questions[questionIndex]?.questionHelper, 
                        inlineChoices: testModules[moduleIndex].questions[questionIndex]?.inlineChoices || [],
                        correctAnswers: testModules[moduleIndex].questions[questionIndex]?.answers || [],
                        studentAnswers: testModules[moduleIndex].answers[questionIndex]?.answer as any[] || []    
                    });
                }
            } 
        }
    }, [testModules, moduleIndex, questionIndex]);

    useEffect(() => {
        if (testModules) {
            renderKatex();
        }
    }, [directionsOpen]);

    useEffect(()=>{
        renderKatex();
    }, [questionHelper, fbQuestionHelper, icQuestionHelper]);
    
    useEffect(() => {
        if (fullPassage) {
            generateAnnotation();
        }
    }, [fullPassage]);

    useEffect(()=>{
        // question helper
        if (testModules) {
            if (testModules[moduleIndex].questions[questionIndex]?.questionHelper) {
                if (testModules[moduleIndex].questions[questionIndex]?.questionType === 4) {
                    generateQuestionHelper({ 
                        questionHelper: testModules[moduleIndex].questions[questionIndex]?.questionHelper, 
                        hotText: testModules[moduleIndex].questions[questionIndex]?.hotText || [],
                        correctAnswers: testModules[moduleIndex].questions[questionIndex]?.hotText?.filter(ht => ht.isCorrect).map(ht => ht.index) || [],
                        studentAnswers: testModules[moduleIndex].answers[questionIndex]?.answer as any[] || []
                    });
                } else if (testModules[moduleIndex].questions[questionIndex]?.questionType === 7) {
                    generateFBQuestionHelper({ 
                        questionHelper: testModules[moduleIndex].questions[questionIndex]?.questionHelper, 
                        correctAnswers: testModules[moduleIndex].questions[questionIndex]?.answers || [],
                        studentAnswers: testModules[moduleIndex].answers[questionIndex]?.answer as any[] || []
                    });
                } else if (testModules[moduleIndex].questions[questionIndex]?.questionType === 9) {
                    generateICQuestionHelper({ 
                        questionHelper: testModules[moduleIndex].questions[questionIndex]?.questionHelper, 
                        inlineChoices: testModules[moduleIndex].questions[questionIndex]?.inlineChoices || [],
                        correctAnswers: testModules[moduleIndex].questions[questionIndex]?.answers || [],
                        studentAnswers: testModules[moduleIndex].answers[questionIndex]?.answer as any[] || []    
                    });
                }
            } 
        }
    }, [showAnswer]);

    if (testModules === null) return <Loader />;

    const {
        name,
        questions,
        answers,
        subject,
        prompt
    } = testModules[moduleIndex];

    return (
        <div id='digitalSHSATReviewBox' className='digitalSHSATReviewBox' onCopy={(event) => { event.preventDefault() }}>
            {/* {explanationVideo &&
                <Popup
                    secondaryButtonText='CLOSE'
                    secondaryButtonOnClick={() => setExplanationVideo(null)}
                    innerRef={explanationRef}
                >
                    <div className='digitalSHSATReview__videoContainer'>
                        <div id='vimeoPlayer'></div>
                    </div>
                </Popup>
            } */}
            <div id='definitionDrag' className={currentDefinition
                ? 'digitalSHSATReview__popup--definition'
                : 'digitalSHSATReview__popup--hidden'
            }
                style={{
                    position: 'absolute',
                    top: popupPos.top,
                    left: popupPos.left
                }}
            >
                <div id='definitionHeader' className='digitalSHSATReview__popupHeader draggable-element'>
                    <h3 className='digitalSHSATReview__popupHeaderText'>Glossary Definition</h3>
                    <IoClose className='digitalSHSATReview__popupHeaderCloseBig' onClick={()=> setCurrentDefinition(null)} />
                </div>
                <div className='digitalSHSATReview__popupDefinition'>
                    {currentDefinition?.definition}
                </div>
            </div>
            {(organization.customizations.find(customization => customization === 'Show Explanation') && (questions[questionIndex].explanation || questions[questionIndex].explanationFigure)) &&
                <div id='explanationDrag' className={explanationOpen
                    ? 'digitalSHSATReview__popup--explanation'
                    : 'digitalSHSATReview__popup--hidden'
                }>
                    <div id='explanationHeader' className='digitalSHSATReview__popupHeader draggable-element'>
                        <h3 className='digitalSHSATReview__popupHeaderText'>Explanation</h3>
                        <IoClose className='digitalSHSATReview__popupHeaderCloseBig' onClick={() => setExplanationOpen(false)} />
                    </div>
                    <div className='digitalSHSATReview__explanationContainer'>
                        {questions[questionIndex].explanationFigure && (
                            <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                <img
                                    src={questions[questionIndex].explanationFigure}
                                    alt='Explanation Figure'
                                    style={{ maxWidth: questions[questionIndex].explanation ? '80%' : '100%', height: 'auto' }}
                                />
                            </div>
                        )}
                        <div className='digitalSHSATReview__explanationBox katex'>
                            {questions[questionIndex].explanation
                                ? questions[questionIndex].explanation
                                : questions[questionIndex].explanationFigure
                                ? ''
                                : 'No explanation'
                            }
                        </div>
                    </div>
                </div>
            }
            <Helmet>
                <title>
                    Reviewing {subject} - {organization.name}
                </title>
            </Helmet>
            <div className='digitalSHSATReview'>
                <div className='digitalSHSATReview__header'>
                    <div className='digitalSHSATReview__titleGroup'>
                        <button className='digitalSHSATReview__moduleSelect' onClick={() => setModuleSelectOpen(!moduleSelectOpen)}>
                            <h1 className='digitalSHSATReview__title'>
                                {subject}
                            </h1>
                            {moduleSelectOpen
                                ? <MdKeyboardArrowUp className='digitalSHSATReview__moduleSelectIcon' />
                                : <MdKeyboardArrowDown className='digitalSHSATReview__moduleSelectIcon' />
                            }
                            {moduleSelectOpen &&
                                <div className='digitalSHSATReview__moduleSelectDropdown' ref={moduleSelectRef}>
                                    {testModules.map((testModule, t) => (
                                        <p
                                            className={t === moduleIndex
                                                ? 'digitalSHSATReview__moduleSelectDropdownItem--selected'
                                                : 'digitalSHSATReview__moduleSelectDropdownItem'
                                            }
                                            onClick={() => {
                                                if (t !== moduleIndex) {
                                                    setModuleIndex(t);
                                                    setQuestionIndex(0);
                                                }
                                            }}
                                        >
                                            {testModule.subject}
                                        </p>
                                    ))}
                                </div>
                            }
                        </button>
                        <button className='digitalSHSATReview__directionsButton' onClick={() => setDirectionsOpen(!directionsOpen)}>
                            {directionsOpen
                                ? <u>Directions</u>
                                : <p>Directions</p>
                            }
                            {directionsOpen
                                ? <MdKeyboardArrowUp className='digitalSHSATReview__directionsIcon' />
                                : <MdKeyboardArrowDown className='digitalSHSATReview__directionsIcon' />
                            }
                        </button>
                    </div>
                    <div className='digitalSHSATReview__timerGroup'>
                        <p className='digitalSHSATReview__timer'>
                            {formatTime(answers[questionIndex].timeSpent)}
                        </p>
                        <p className='digitalSHSATReview__timerText'>
                            Time spent
                        </p>
                    </div>
                    <div className='digitalSHSATReview__actionGroup'>
                        <button
                            className='digitalSHSATReview__action'
                            onClick={() => navigate(-1)}
                            // onClick={() => navigate(`/report/digital-sat/${testId}`)}
                        >
                            <MdExitToApp className='digitalSHSATReview__actionIcon' />
                            <p className='digitalSHSATReview__actionText'>Exit</p>
                        </button>
                    </div>
                    {directionsOpen &&
                        <div className='digitalSHSATReview__directions' ref={directionsRef}>
                            <div className='digitalSHSATReview__directionsBubble'>
                                <p className='katex digitalSHSATReview__directionsText'>
                                    {prompt
                                        ? prompt
                                        : 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Duis sollicitudin, mi congue condimentum dignissim, ipsum diam tristique velit, et rutrum orci massa non purus. Etiam vitae ante sed velit blandit condimentum. Donec lectus ipsum, volutpat vel tortor a, blandit elementum felis. Mauris a ultricies quam, at molestie ipsum. Cras nec ante justo. Suspendisse egestas lectus sit amet sem maximus auctor. In ut dignissim lorem, ac tristique eros. Nullam eget sollicitudin quam. Quisque pellentesque cursus justo sed ornare. Integer finibus, tortor at facilisis molestie, ligula augue cursus ipsum, eget auctor enim sem a eros. Pellentesque habitant morbi tristique senectus et netus et malesuada fames ac turpis egestas. Pellentesque suscipit, nunc eget mollis consequat, augue odio suscipit enim, vel varius eros leo sit amet magna. Nunc suscipit leo et metus bibendum, a finibus libero finibus. In lacinia scelerisque urna ut pretium. In at est aliquam, dictum odio non, fringilla tellus. Vivamus commodo molestie urna at dapibus. Nulla facilisi. Maecenas sit amet lorem tincidunt, maximus nunc eu, malesuada velit. Class aptent taciti sociosqu ad litora torquent per conubia nostra, per inceptos himenaeos. Nulla facilisi.'
                                    }
                                </p>
                                <Button
                                    className='digitalSHSATReview__directionsClose'
                                    onClick={() => setDirectionsOpen(false)}
                                    secondary
                                >
                                    CLOSE
                                </Button>
                            </div>
                        </div>
                    }
                </div>
                <div className='digitalSHSATReview__dottedLine'></div>
                <div className='digitalSHSATReview__test'>
                    {questions[questionIndex].passages && <>
                        <div className='digitalSHSATReview__material'>
                            <div className='digitalSHSATReview__materialContent'>
                                <div key={questions[questionIndex].passages.id} id='digitalSHSATReview__passage' className='digitalSHSATReview__passage'>
                                    {/* 해당 id를 가진 passage의 p태그 가져와서 highlight 넣은 html 받아와서 겹쳐 보여줌 (z-index, transparent 조절) */}
                                    <p
                                        id={questions[questionIndex].passages.id}
                                        className='katex digitalSHSATReview__passageText'
                                    >
                                        {questions[questionIndex].passages.passage}
                                    </p>
                                    <p
                                        id={`passage ${questions[questionIndex].passages.id}`}
                                        className='katex digitalSHSATReview__passageTextPassage'
                                        dangerouslySetInnerHTML={{ __html: fullPassage?.html }}
                                    >
                                    </p>
                                    {/* annotation */}
                                    {
                                        answers[questionIndex].annotations.filter(annotation => (
                                            annotation.passageId === `${questions[questionIndex].passages.id}`
                                        )).map((annotation, a) => <>
                                            <div
                                                className={`katex digitalSHSATReview__passageAnnotation
                                                        ${annotation.color && 'digitalSHSATReview__passageAnnotation--' + COLORS[annotation.color]}
                                                        `}
                                                dangerouslySetInnerHTML={{ __html: annotation.html }}
                                            ></div>
                                        </>)
                                    }
                                    {/* glossary definition */}
                                    {
                                        definitions.map((definition, index)=>
                                            <div
                                                id={`definition ${questions[questionIndex].passages.id}`}
                                                key={index}
                                                className='katex digitalSHSATReview__Definition'
                                                onMouseUp={handleDefinition}    
                                                onTouchEnd={handleDefinition}  
                                                dangerouslySetInnerHTML={{ __html: definition?.html }}
                                            ></div>
                                        )
                                    }
                                </div>
                            </div>
                        </div>
                        <div className='digitalSHSATReview__verticalLine'></div>
                    </>}
                    <div className='digitalSHSATReview__question'>
                        <div className='digitalSHSATReview__questionContent'>
                            <div className='digitalSHSATReview__questionHeader'>
                                <p className={answers[questionIndex].isCorrect
                                    ? 'digitalSHSATReview__questionNumber--correct'
                                    : 'digitalSHSATReview__questionNumber--wrong'
                                }>
                                    {questions[questionIndex].questionNum}
                                </p>
                                <button className='digitalSHSATReview__questionMarkButton' onClick={() => {}}>
                                    {answers[questionIndex].marked
                                        ? <MdOutlineBookmark className='digitalSHSATReview__questionMarkIcon--marked' />
                                        : <MdOutlineBookmarkBorder className='digitalSHSATReview__questionMarkIcon' />
                                    }
                                    Bookmark
                                </button>
                                {organization.customizations.find(customization => customization === 'Enable Show Answer') &&
                                    <button
                                        className={showAnswer
                                            ? 'digitalSHSATReview__showAnswer--active'
                                            : 'digitalSHSATReview__showAnswer'
                                        }
                                        onClick={() => setShowAnswer(!showAnswer)}
                                    >
                                        {showAnswer ? 'Hide answer' : 'Show answer'}
                                    </button>
                                }
                                {(organization.customizations.find(customization => customization === 'Show Explanation') && (questions[questionIndex].explanation || questions[questionIndex].explanationFigure)) &&
                                    <button
                                        className={explanationOpen
                                            ? 'digitalSHSATReview__showExplanation--active'
                                            : 'digitalSHSATReview__showExplanation'
                                        }
                                        onClick={() => setExplanationOpen(!explanationOpen)}
                                    >
                                        Explanation
                                    </button>
                                }
                            </div>
                            <div id='digitalSHSATReview__questionChoices' className='digitalSHSATReview__questionChoices'>
                                <p className='digitalSHSATReview__questionQuestionContainer'>
                                    {/* Skip the raw stem when it carries blank markers
                                        (``[[ ]]``); the helper re-renders it with inputs.
                                        Mirrors the student AdaptiveModule guard. */}
                                    {questions[questionIndex].question && !questions[questionIndex].question.includes('[[') &&
                                        <span className='katex digitalSHSATReview__questionQuestion'>
                                            {questions[questionIndex].question}
                                        </span>
                                    }
                                    {questions[questionIndex].figures &&
                                        questions[questionIndex].figures.map(figure => (
                                            <img
                                                className='digitalSHSATReview__figure'
                                                key={figure.id}
                                                src={figure.image}
                                                alt={(questions[questionIndex].questionNum).toString()}
                                            />
                                        ))
                                    }
                                </p>
                                {questions[questionIndex].questionType===1
                                ? <div className='digitalSHSATReview__questionContainer'>
                                    {questions[questionIndex].choices &&   
                                        <>
                                            {questions[questionIndex].choices.map((choice, i) => (
                                                <div key={choice.id}  
                                                    className='digitalSHSATReview__questionChoiceWrapper'
                                                >
                                                    <div className='digitalSHSATReview__choiceContainer'>
                                                        <div
                                                            className={showAnswer && choice.isCorrect
                                                                ? 'digitalSHSATReview__radioButton--correct'
                                                                : answers[questionIndex].answer === choice.id
                                                                ? (choice.isCorrect 
                                                                    ? 'digitalSHSATReview__radioButton--selected'
                                                                    : 'digitalSHSATReview__radioButton--wrong'
                                                                )
                                                                : 'digitalSHSATReview__radioButton'
                                                            }>
                                                                {showAnswer && choice.isCorrect || answers[questionIndex].answer === choice.id?
                                                                    <IoMdRadioButtonOn className='digitalSHSATReview__radioButtonIcon'/>
                                                                    : <IoMdRadioButtonOff className='digitalSHSATReview__radioButtonIcon'/>
                                                                }
                                                        </div>
                                                        <div className={showAnswer && choice.isCorrect
                                                            ? 'digitalSHSATReview__questionChoiceContent--correct'
                                                            : answers[questionIndex].answer == choice.id
                                                            ? (choice.isCorrect 
                                                                ? 'digitalSHSATReview__questionChoiceContent--selected'
                                                                : 'digitalSHSATReview__questionChoiceContent--wrong'
                                                            )
                                                            : 'digitalSHSATReview__questionChoiceContent'
                                                        }>
                                                            <p className={showAnswer && choice.isCorrect
                                                                ? 'digitalSHSATReview__choiceText--correct'
                                                                : answers[questionIndex].answer == choice.id
                                                                ? (choice.isCorrect 
                                                                    ? 'digitalSHSATReview__choiceText--selected'
                                                                    : 'digitalSHSATReview__choiceText--wrong'
                                                                )
                                                                : 'digitalSHSATReview__choiceText'
                                                            }>
                                                                {MULTIPLE_CHOICES[i]}.
                                                            </p>
                                                            {choice.figure
                                                            ?   <img
                                                                    className='digitalSHSATReview__questionChoiceFigure'
                                                                    key={choice.figure}
                                                                    src={choice.figure}
                                                                    alt={choice.figure}
                                                                />
                                                            :   <p className='digitalSHSATReview__questionChoiceText'>
                                                                    <span className='katex'>
                                                                        {choice.choice}
                                                                    </span>
                                                                </p>
                                                            }
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </>
                                    }
                                </div>
                                : questions[questionIndex].questionType===3
                                ? <div className='digitalSHSATReview__questionContainer'>
                                    {questions[questionIndex].choices.length &&   
                                        <>
                                            {questions[questionIndex].choices.map((choice, i) => (
                                                <div key={choice.id}  
                                                    className='digitalSHSATReview__questionChoiceWrapper'
                                                >
                                                    <div className='digitalSHSATReview__choiceContainer'>
                                                        <div className={showAnswer && choice.isCorrect
                                                            ? 'digitalSHSATReview__radioButton--correct'
                                                            : answers[questionIndex].answer.includes(choice.id)
                                                            ? (choice.isCorrect 
                                                                ? 'digitalSHSATReview__radioButton--selected'
                                                                : 'digitalSHSATReview__radioButton--wrong'
                                                            )
                                                            : 'digitalSHSATReview__radioButton'
                                                        }>
                                                            {showAnswer && choice.isCorrect || (Array.isArray(answers[questionIndex].answer) && answers[questionIndex].answer.includes(choice.id))?
                                                                <RiCheckboxFill className='digitalSHSATReview__radioButtonIcon'/>
                                                                : <RiCheckboxBlankLine className='digitalSHSATReview__radioButtonIcon'/>
                                                            }
                                                        </div>
                                                        <div className={showAnswer && choice.isCorrect
                                                            ? 'digitalSHSATReview__questionChoiceContent--correct'
                                                            : answers[questionIndex].answer.includes(choice.id)
                                                            ? (choice.isCorrect 
                                                                ? 'digitalSHSATReview__questionChoiceContent--selected'
                                                                : 'digitalSHSATReview__questionChoiceContent--wrong'
                                                            )
                                                            : 'digitalSHSATReview__questionChoiceContent'
                                                        }>
                                                            <p className={showAnswer && choice.isCorrect
                                                                ? 'digitalSHSATReview__choiceText--correct'
                                                                : answers[questionIndex].answer.includes(choice.id)
                                                                ? (choice.isCorrect 
                                                                    ? 'digitalSHSATReview__choiceText--selected'
                                                                    : 'digitalSHSATReview__choiceText--wrong'
                                                                )
                                                                : 'digitalSHSATReview__choiceText'
                                                            }>
                                                                {MULTIPLE_CHOICES[i]}.
                                                            </p>
                                                            {choice.figure
                                                            ?   <img
                                                                    className='digitalSHSATReview__questionChoiceFigure'
                                                                    key={choice.figure}
                                                                    src={choice.figure}
                                                                    alt={choice.figure}
                                                                />
                                                            :   <p className='digitalSHSATReview__questionChoiceText'>
                                                                    <span className='katex'>
                                                                        {choice.choice}
                                                                    </span>
                                                                </p>
                                                            }
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </>
                                    }
                                </div>
                                : questions[questionIndex].questionType===4
                                ? <div className='digitalSHSATReview__questionContainer'>
                                    {questions[questionIndex]?.hotText?.length > 0 && 
                                        <div id='digitalSHSATReview__questionHelper' className='digitalSHSATReview__questionChoiceWrapper'>
                                            <p
                                                className='katex digitalSHSATReview__questionHelperText'
                                                dangerouslySetInnerHTML={{ __html: questionHelper }}
                                            ></p>
                                        </div>
                                    }
                                </div>
                                : questions[questionIndex].questionType===5
                                ? <div className='digitalSHSATReview__questionContainer'>
                                    <DragAndDropBucketReview 
                                        question={questions[questionIndex]}
                                        answers={answers[questionIndex].answer}
                                        showAnswer={showAnswer}
                                    />
                                </div>
                                : questions[questionIndex].questionType===6
                                ? <div className='digitalSHSATReview__questionContainer'>
                                    <DragAndDropBlankReview
                                        question={questions[questionIndex]}
                                        answers={answers[questionIndex].answer}
                                        showAnswer={showAnswer}
                                    />
                                </div>
                                : questions[questionIndex].questionType===7
                                ? <div className='digitalSHSATReview__questionContainer'>
                                    <div className='digitalSHSATReview__questionChoiceWrapper'>
                                        <p
                                            className='katex digitalSHSATReview__questionHelperText'
                                            dangerouslySetInnerHTML={{ __html: fbQuestionHelper }}
                                        ></p>
                                    </div>
                                </div>
                                : questions[questionIndex].questionType===8
                                ? <div className='digitalSHSATReview__questionContainer'>
                                    <TableGridReview
                                        question={questions[questionIndex]}
                                        answers={answers[questionIndex].answer}
                                        showAnswer={showAnswer}
                                    />
                                </div>
                                : questions[questionIndex].questionType===9
                                ? <div className='digitalSHSATReview__questionContainer'>
                                    <div className='digitalSHSATReview__questionChoiceWrapper'>
                                        <p
                                            className='katex digitalSHSATReview__questionHelperText'
                                            dangerouslySetInnerHTML={{ __html: icQuestionHelper }}
                                        ></p>
                                    </div>
                                </div>
                                : questions[questionIndex].questionType===10
                                ? <div className='digitalSHSATReview__questionContainerHotSpot'>
                                    {questions[questionIndex].choices.length &&   
                                        <>
                                            {questions[questionIndex].choices?.map((choice, i) => (
                                                <div 
                                                    className={Array.isArray(answers[questionIndex].answer) && 
                                                        showAnswer && choice.isCorrect
                                                        ? 'digitalSHSATReview__choiceContainerHotSpot--correct'
                                                        : answers[questionIndex].answer.includes(choice.id)
                                                        ? (choice.isCorrect
                                                            ? 'digitalSHSATReview__choiceContainerHotSpot--correct'
                                                            : 'digitalSHSATReview__choiceContainerHotSpot--wrong'
                                                        )
                                                        : 'digitalSHSATReview__choiceContainerHotSpot'
                                                    }
                                                >
                                                    {choice.figure
                                                    ?   <img
                                                            className='digitalSHSATReview__questionChoiceFigure'
                                                            key={choice.figure}
                                                            src={choice.figure} 
                                                            alt={choice.figure}
                                                            draggable="false"
                                                        />
                                                    :   <p className='digitalSHSATReview__questionChoiceText'>
                                                            <span className='katex'>
                                                                {choice.choice}
                                                            </span>
                                                        </p>
                                                    }
                                                </div>
                                            ))}
                                        </>
                                    }
                                </div>
                                : questions[questionIndex].questionType===11 || questions[questionIndex].questionType===12
                                ? <div className='digitalSHSATReview__questionContainer'>
                                    <div className={`digitalSHSATReview__eeContainer${navigatorOpen ? ' digitalSHSATReview__eeContainer--navOpen' : ''}`}>
                                        <math-field
                                            ref={mathfieldRef}
                                            style={{
                                                width: "620px",
                                                height: "50px",
                                                fontSize: "20px"
                                            }}
                                            className='digitalSHSATReview__eeInput'
                                            virtual-keyboard-mode="manual"
                                        >
                                        </math-field>
                                    </div>
                                </div>
                                :<></>
                                }
                                <div className='digitalSHSATReview__marginBottom'></div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='digitalSHSATReview__dottedLine'></div>
                <div className='digitalSHSATReview__footer'>
                    {/* {questions[questionIndex].explanationVideo
                    ?   <div className='digitalSHSATReview__buttons'>
                            <Button
                                className='digitalSHSATReview__explanationButton'
                                onClick={() => setExplanationVideo(questions[questionIndex].explanationVideo)}
                            >
                                SOLUTION VIDEO
                                <MdOutlinePlayCircle className='digitalSHSATReview__explanationButtonIcon' />
                            </Button>
                        </div>
                    :    <p className='digitalSHSATReview__tester'>
                            {studentName}
                        </p>
                    } */}
                    <p className='digitalSHSATReview__tester'>
                        {studentName}
                    </p>
                    <button
                        className='digitalSHSATReview__navigatorToggle'
                        onClick={() => setNavigatorOpen(!navigatorOpen)}
                    >
                        Question {questions[questionIndex].questionNum}/{testModules.reduce((sum, module) => sum + module.questions.length, 0)}
                        {navigatorOpen
                            ? <MdKeyboardArrowDown className='digitalSHSATReview__navigatorToggleIcon' />
                            : <MdKeyboardArrowUp className='digitalSHSATReview__navigatorToggleIcon' />
                        }
                    </button>
                    <div className='digitalSHSATReview__buttons'>
                        <Button
                            className='digitalSHSATReview__button'
                            secondary
                            disabled={moduleIndex === 0 && questionIndex === 0}
                            onClick={prevQuestion}
                        >
                            BACK
                        </Button>
                        <Button
                            className='digitalSHSATReview__button'
                            onClick={nextQuestion}
                        >
                            NEXT
                        </Button>
                    </div>
                    {navigatorOpen &&
                        <div className='digitalSHSATReview__navigator' ref={navigatorRef}>
                            <div className='digitalSHSATReview__navigatorBubble'>
                                <h2 className='digitalSHSATReview__navigatorTitle'>
                                    {name} Questions
                                </h2>
                                <div className='digitalSHSATReview__navigatorLegends'>
                                    <div className='digitalSHSATReview__navigatorLegend'>
                                        <MdOutlineLocationOn className='digitalSHSATReview__navigatorLegendCurrent' />
                                        <p className='digitalSHSATReview__navigatorLegendText'>
                                            Current
                                        </p>
                                    </div>
                                    <div className='digitalSHSATReview__navigatorLegend'>
                                        <div className='digitalSHSATReview__navigatorLegendCorrect'></div>
                                        <p className='digitalSHSATReview__navigatorLegendText'>
                                            Correct
                                        </p>
                                    </div>
                                    <div className='digitalSHSATReview__navigatorLegend'>
                                        <div className='digitalSHSATReview__navigatorLegendWrong'></div>
                                        <p className='digitalSHSATReview__navigatorLegendText'>
                                            Incorrect
                                        </p>
                                    </div>
                                    <div className='digitalSHSATReview__navigatorLegend'>
                                        <MdOutlineBookmark className='digitalSHSATReview__navigatorLegendMarked' />
                                        <p className='digitalSHSATReview__navigatorLegendText'>
                                            For review
                                        </p>
                                    </div>
                                </div>
                                <div className='digitalSHSATReview__navigatorItems'>
                                    {questions.map((question, i) => (
                                        <button
                                            key={`${question.id}nav`}
                                            className={answers[i].isCorrect
                                                ? 'digitalSHSATReview__navigatorItem--correct'
                                                : 'digitalSHSATReview__navigatorItem--wrong'
                                            }
                                            onClick={() => {
                                                goToQuestion(i);
                                                setNavigatorOpen(false);
                                            }}
                                        >
                                            {questionIndex === i
                                                ? <u className='digitalSHSATReview__navigatorItemNumber'>{question.questionNum}</u>
                                                : <p className='digitalSHSATReview__navigatorItemNumber'>{question.questionNum}</p>
                                            }
                                            {questionIndex === i && <MdOutlineLocationOn className='digitalSHSATReview__navigatorItemCurrent' />}
                                            {answers[i].marked && <MdOutlineBookmark className='digitalSHSATReview__navigatorItemMarked' />}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    }
                </div>
            </div>
        </div>
    );
}

export default DigitalSHSATReview;